/**
 * Decision 0114 (Dictly session 2 review, fix 8): facts that tell an agent a page no longer says what is true. Pure;
 * the cloud's turn file turns them into one-line nudges (never a rewrite), the web shows the empty mandate.
 *
 * - {@link mandateIsEmpty}: the agent's mandate is missing or still the template the pages job wrote.
 * - {@link mandateChangedSince}: a person changed the agent's mandate after `since` (its last turn ended).
 * - {@link staleStatusPages}: the status pages an agent keeps are older than state changes they describe.
 */
import { MANDATE_TEMPLATE } from "@minga/protocol"
import { sha256Hex } from "./hash"
import { versionsOf } from "./outputs"
import { areaStatusOf, areasLedBy, pageOf } from "./pages"
import { agentOf, leadOf } from "./state"
import { ancestorsOf } from "./tree"
import type { AgentId, Artifact, Item, ProjectState } from "./types"

/**
 * The agent's mandate head is the pages job's template (only `system{pages}` writes a mandate without a person; agents
 * cannot write one), or a person saved the template unchanged. False for an unknown or retired agent and while the
 * page is missing (that is the `pages:` loose end's).
 */
export const mandateIsEmpty = (s: ProjectState, agentId: AgentId): boolean => {
  const agent = agentOf(s, agentId)
  if (!agent || agent.status === "retired") return false
  const page = pageOf(s, "mandate", agentId)
  if (!page) return false
  if (page.createdBy._tag === "system") return true
  const project = s.project?.name ?? ""
  return (
    page.sha256 !== null &&
    (page.sha256 === sha256Hex(MANDATE_TEMPLATE(agent.handle, project)) ||
      page.sha256 === sha256Hex(MANDATE_TEMPLATE(agent.handle, project, true)))
  )
}

/** The agent's mandate head when a person wrote it after `since` (null: never ran, no line). */
export const mandateChangedSince = (
  s: ProjectState,
  agentId: AgentId,
  since: number | null | undefined,
): { readonly page: Artifact; readonly version: number } | null => {
  if (since === null || since === undefined) return null
  const page = pageOf(s, "mandate", agentId)
  if (page?.createdBy._tag !== "user" || page.createdAt <= since) return null
  return { page, version: versionsOf(s, page).length }
}

/** A status page older than the state changes it describes. */
export interface StaleStatus {
  readonly path: string
  readonly ref: string
  readonly version: number
  readonly started: number
  readonly closed: number
  readonly cards: number
}

const inArea = (s: ProjectState, t: Item, areaId: string): boolean =>
  [t, ...ancestorsOf(s, t)].some((x) => x.areaId === areaId)

const APPLIED: ReadonlySet<string> = new Set(["applied", "answered"])

const staleness = (
  s: ProjectState,
  page: Artifact,
  items: ReadonlyArray<Item>,
  withCards: boolean,
): StaleStatus | null => {
  const since = page.createdAt
  const started = items.filter((t) => t.startedAt !== null && t.startedAt > since).length
  const closed = items.filter((t) => t.closedAt !== null && t.closedAt > since).length
  const cards = withCards
    ? s.proposals.filter((p) => APPLIED.has(p.state) && (p.resolution?.at ?? 0) > since).length
    : 0
  if (started + closed + cards === 0) return null
  return {
    path: page.doc?.path ?? page.title,
    ref: page.ref ?? page.id,
    version: versionsOf(s, page).length,
    started,
    closed,
    cards,
  }
}

/**
 * The status pages `agentId` keeps that are older than what they describe: `project/status.md` for the project lead
 * (items started or closed and cards applied or answered since its head), and each area status page it leads (items in
 * that area started or closed since). Pages that do not exist yet are the `pages:` loose end's, not this.
 */
export const staleStatusPages = (s: ProjectState, agentId: AgentId): ReadonlyArray<StaleStatus> => {
  const out: StaleStatus[] = []
  if (leadOf(s)?.id === agentId) {
    const status = pageOf(s, "status")
    const stale = status ? staleness(s, status, s.items, true) : null
    if (stale) out.push(stale)
  }
  for (const area of areasLedBy(s, agentId)) {
    const page = areaStatusOf(s, area)
    const stale = page
      ? staleness(
          s,
          page,
          s.items.filter((t) => inArea(s, t, area.id)),
          false,
        )
      : null
    if (stale) out.push(stale)
  }
  return out
}
